import { useCallback, useEffect, useState, useRef, forwardRef, useMemo } from 'react';
import { Stack, Box, Group, Button, ActionIcon, Text, Loader, Alert, Popover, Modal, Textarea } from '@mantine/core';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { Icon } from '../Icon/Icon';
import { useNavigate } from '@tanstack/react-router';
import { UseQueryResult } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useChatActions, isChatSendInFlight } from '../../../../hooks/useSendMessage';
import { FEATURES } from '../../../../config/featureFlags';
import { useChatStore } from '../../../../stores/chatStore';
import { useEditMessage } from '../../../../hooks/useEditMessage';
import { useStreamingStore } from '../../../../stores/streamingStore';
import { useIsMessageSending } from '../../../../hooks';
import { useAgentSelector } from '../../../../hooks';
import { useAnnouncements } from '../../../../hooks';
import type { ConversationMessage } from '../../types/conversation';
import type { ConversationDetailResponse } from '../../../../api/conversations';
import { getAllConversationFiles } from '../../../../api/conversations';
import { getUserMessageCount } from '../../../../utils/messageUtils';
import { AgentTag } from './AgentTag';
import { AgentsModal, AtAgentsModal, type AtAgentsModalRef, type Agent } from '../AgentsModal';
import { useAgents } from '../../../../api/agents';
import { ModelsModal, type Model } from '../ModelsModal';
import { PromptLibraryModal, type PromptTemplate } from '../PromptLibraryModal';
import { AttachModal } from '../AttachModal';
import { AnnouncementModal } from '../AnnouncementModal';
import { UploadedFileIndicators } from './UploadedFileIndicators';
import { FileDropzone } from '../FileDropzone';
import { getDropzoneAccept, MAX_FILES, MAX_FILE_SIZE_BYTES, validateAgainstLimits, validateFileTypes } from '../../utils/fileUploadValidation';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import { useFileDelete } from '../../../../hooks/useFileDelete';
import { useUploadStatusPoll } from '../../../../hooks/useConversationDetail';
import { showNotification } from '../../../../components/Notification';
import { conversationQueryKeys } from '../../../../api/conversations';
import { useQueryClient } from '@tanstack/react-query';
import classes from './ChatInputSimple.module.css';
import {DEFAULT_MODEL} from '../../../../config/defaults';
import { getSelectableModelDisplayName } from '../../../../config/models';
import { trackComponentInteraction } from '../../../../utils/analytics';
import { getFilesWithUniqueNames } from '../../../../utils/fileNameUtils';
import { useAgentsStore } from '../../../../stores/agentsStore';
import { useUserSettingsStore } from '../../../../stores/userSettingsStore';
import { set } from 'date-fns';

interface ChatInputSimpleProps {
  conversationId: string;
  disabled?: boolean;
  placeholder?: string;
  showControls?: boolean;
  query?: UseQueryResult<ConversationDetailResponse>;
  onMessageSent?: (conversationId: string) => void;
}

const CONTROL_POPOVER_VIEWPORT_PADDING = {
  top: 48,
  right: 12,
  bottom: 12,
  left: 12,
};

export const ChatInputSimple = forwardRef<HTMLTextAreaElement, ChatInputSimpleProps>(({
  conversationId,
  disabled = false,
  placeholder = "Hi there! How can I help you today?",
  showControls = true,
  query,
  onMessageSent
}, ref) => {
  // Local state for agent tag hover
  const [isAgentTagHovered, setIsAgentTagHovered] = useState(false);

  // Track upload state to prevent modal closing during uploads
  const [isModalUploading, setIsModalUploading] = useState(false);
  const [isModalDeleting, setIsModalDeleting] = useState(false);

  // Inline drag & drop state
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragDepthRef = useRef(0);
  const [dropErrorMessage, setDropErrorMessage] = useState<string | undefined>();

  // Internal ref for the textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionCaretPosition, setMentionCaretPosition] = useState<number | null>(null);

  // Ref for the AtAgentsModal to control keyboard interactions
  const atAgentsModalRef = useRef<AtAgentsModalRef>(null);

  // Merge internal ref with forwarded ref
  const setRef = useCallback((node: HTMLTextAreaElement | null) => {
    // Update internal ref
    (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;

    // Update forwarded ref
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }
  }, [ref]);

  // Navigation hook for auto-navigation after successful message send
  const navigate = useNavigate();

  // Create navigation callback for seamless conversation transition
  const handleAutoNavigation = useCallback(() => {
    navigate({ to: '/chat/$conversationId', params: { conversationId } });
  }, [navigate, conversationId]);

  // Get mutation hook for sending messages with navigation callback
  const { sendMessage, error, canSend, reset, isLoading: isMutationPending, abortRequest } = useChatActions(conversationId, handleAutoNavigation);

  // Get edit message hook for abort functionality during editing
  const { abortRequest: abortEditRequest } = useEditMessage(conversationId);

  // Check if any message is currently being sent across the entire app
  const isMessageSending = useIsMessageSending();

  // Get streaming state and stop functionality
  const { isStreaming, stopThinking } = useStreamingStore();

  // Refs kept in sync with the above so the fileProcessingStatus effect can
  // read their *current* values without needing them as dependencies.  This
  // prevents the effect re-running (and re-issuing invalidateQueries) whenever
  // streaming ends while fileProcessingStatus is still 'complete'.
  const isMessageSendingRef = useRef(isMessageSending);
  const isStreamingRef = useRef(isStreaming);
  useEffect(() => { isMessageSendingRef.current = isMessageSending; }, [isMessageSending]);
  useEffect(() => { isStreamingRef.current = isStreaming; }, [isStreaming]);

  const {mentionFilter, removeAtInput} = useAgentSelector(conversationId, mentionCaretPosition);

  const { activeUnseenAnnouncements } = useAnnouncements();
  // Fetch agents from API
  const { data: agents = [] } = useAgents();

  // Get chat store state
  const {
    isAgentsModalOpen,
    isAtAgentsModalOpen,
    isModelsModalOpen,
    isPromptLibraryModalOpen,
    isAttachModalOpen,
    setInputValue,
    clearInput,
    openAgentsModal,
    closeAgentsModal,
    closeAtAgentsModal,
    toggleAgentsModal,
    toggleAtAgentsModal,
    openModelsModal,
    closeModelsModal,
    toggleModelsModal,
    openPromptLibraryModal,
    closePromptLibraryModal,
    openAttachModal,
    closeAttachModal,
    setSelectedAgents,
    removeSelectedAgent,
    setSelectedModel,
    addAttachment,
    clearAttachments,
    setUploadSessionId,
    setHomeScreenConversation,
    updateHomeScreenConversation,
    clearHomeScreenConversation,
    // Add conversation file sync for eventual consistency
    syncConversationFiles,
    // Uploaded file indicators support
    removePendingFileForMessage,
    getPendingFilesForMessage,
    addPendingFileForMessage,
    // Conversation files operations
    addConversationFile,
    removeConversationFile,
    removeFile,
    addOrQueueFile,
    markFileUploading,
    markFileUploaded,
    markFileError,
    markFileDeleting,
    getConversationFileEntries,
    setConversationUploadSession,
    // Conversation-specific state getters
    getSelectedAgents,
    getSelectedModel,
    // Character count methods
    getCharacterCount,
    setCharacterCount,
    isCharacterCountVisible,
    isCharacterLimitExceeded,
    // Message count methods (for validation only - no rendering)
    getMessageCount,
    setMessageCount,
    getConversationFiles,
    // Editing state management
    isEditingMessage,
    currentEditingMessageId,
  } = useChatStore(useShallow((state) => ({
    isAgentsModalOpen: state.isAgentsModalOpen,
    isAtAgentsModalOpen: state.isAtAgentsModalOpen,
    isModelsModalOpen: state.isModelsModalOpen,
    isPromptLibraryModalOpen: state.isPromptLibraryModalOpen,
    isAttachModalOpen: state.isAttachModalOpen,
    setInputValue: state.setInputValue,
    clearInput: state.clearInput,
    openAgentsModal: state.openAgentsModal,
    closeAgentsModal: state.closeAgentsModal,
    toggleAgentsModal: state.toggleAgentsModal,
    toggleAtAgentsModal: state.toggleAtAgentsModal,
    closeAtAgentsModal: state.closeAtAgentsModal,
    openModelsModal: state.openModelsModal,
    closeModelsModal: state.closeModelsModal,
    toggleModelsModal: state.toggleModelsModal,
    openPromptLibraryModal: state.openPromptLibraryModal,
    closePromptLibraryModal: state.closePromptLibraryModal,
    openAttachModal: state.openAttachModal,
    closeAttachModal: state.closeAttachModal,
    setSelectedAgents: state.setSelectedAgents,
    removeSelectedAgent: state.removeSelectedAgent,
    setSelectedModel: state.setSelectedModel,
    addAttachment: state.addAttachment,
    removeAttachment: state.removeAttachment,
    updateAttachmentStatus: state.updateAttachmentStatus,
    clearAttachments: state.clearAttachments,
    setUploadSessionId: state.setUploadSessionId,
    clearUploadSession: state.clearUploadSession,
    markAttachmentsAsUploaded: state.markAttachmentsAsUploaded,
    markAttachmentAsUploading: state.markAttachmentAsUploading,
    markAttachmentAsError: state.markAttachmentAsError,
    setStagingState: state.setStagingState,
    getStagedForUpload: state.getStagedForUpload,
    hasStagedUploads: state.hasStagedUploads,
    addConversationFile: state.addConversationFile,
    removeConversationFile: state.removeConversationFile,
    removeFile: state.removeFile,
    addOrQueueFile: state.addOrQueueFile,
    markFileUploading: state.markFileUploading,
    markFileUploaded: state.markFileUploaded,
    markFileError: state.markFileError,
    markFileDeleting: state.markFileDeleting,
    getConversationFileEntries: state.getConversationFileEntries,
    getConversationFiles: state.getConversationFiles,
    setConversationUploadSession: state.setConversationUploadSession,
    clearConversationUploadSession: state.clearConversationUploadSession,
    syncConversationFiles: state.syncConversationFiles,
    hasUnsyncedFiles: state.hasUnsyncedFiles,
    addPendingFileForMessage: state.addPendingFileForMessage,
    removePendingFileForMessage: state.removePendingFileForMessage,
    getPendingFilesForMessage: state.getPendingFilesForMessage,
    clearPendingFilesForMessage: state.clearPendingFilesForMessage,
    setIsComposing: state.setIsComposing,
    setHomeScreenConversation: state.setHomeScreenConversation,
    updateHomeScreenConversation: state.updateHomeScreenConversation,
    clearHomeScreenConversation: state.clearHomeScreenConversation,
    getSelectedAgents: state.getSelectedAgents,
    getSelectedModel: state.getSelectedModel,
    // Character count methods
    getCharacterCount: state.getCharacterCount,
    setCharacterCount: state.setCharacterCount,
    isCharacterCountVisible: state.isCharacterCountVisible,
    isCharacterLimitExceeded: state.isCharacterLimitExceeded,
    // Message count methods (for validation only - no rendering)
    getMessageCount: state.getMessageCount,
    setMessageCount: state.setMessageCount,
    // Editing state management
    isEditingMessage: state.isEditingMessage,
    currentEditingMessageId: state.currentEditingMessageId,
  })));

  // Subscribe to the value itself so mutation updates render the banner
  // immediately instead of waiting for an unrelated state update.
  const fileProcessingStatus = useChatStore(
    (state) => state.fileProcessingStatus[conversationId]?.status ?? null
  );

  // Get announcement store state
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const openAnnouncementModal = useCallback(() => setIsAnnouncementModalOpen(true), []);
  const closeAnnouncementModal = useCallback(() => setIsAnnouncementModalOpen(false), []);

  const markAnnouncementAsSeen = useUserSettingsStore((state) => state.markAnnouncementAsSeen);

  const firstUnseenAnnouncement = activeUnseenAnnouncements[0];
  const firstUnseenAnnouncementId = firstUnseenAnnouncement?.announcementID;

  // Get conversation-specific state
  const inputValue = useChatStore(
    (state) => state.conversationInputs[conversationId]?.inputValue ?? ''
  );
  const selectedAgents = getSelectedAgents(conversationId);
  const effectiveSelectedAgents = useMemo(
    () => (selectedAgents.includes('image') ? ['image'] : selectedAgents),
    [selectedAgents]
  );
  const selectedModel = getSelectedModel(conversationId);

  // Keep state deterministic for image routing: image cannot be combined
  useEffect(() => {
    if (selectedAgents.includes('image') && selectedAgents.length > 1) {
      setSelectedAgents(conversationId, ['image']);
    }
  }, [selectedAgents, setSelectedAgents, conversationId]);

  // Character count state
  const characterCount = getCharacterCount(conversationId);
  const showCharacterCounter = isCharacterCountVisible(conversationId);
  const isOverLimit = isCharacterLimitExceeded(conversationId);

  // Display Active Announcement if there is an active announcement the user has not seen
  const handleAnnouncementClose = useCallback(() => {
    if (firstUnseenAnnouncementId) {
      void markAnnouncementAsSeen(firstUnseenAnnouncementId);
    }
    if (firstUnseenAnnouncement) {
      trackComponentInteraction(
        'ChatInputSimple',
        firstUnseenAnnouncement.title,
        'announcement',
        'dismiss',
        firstUnseenAnnouncement.announcementID
      );
    }
    closeAnnouncementModal();
  }, [firstUnseenAnnouncement, firstUnseenAnnouncementId, markAnnouncementAsSeen, closeAnnouncementModal]);

  useEffect(() => {
    if (activeUnseenAnnouncements.length > 0) {
      openAnnouncementModal();
    }
  }, [activeUnseenAnnouncements, openAnnouncementModal]);

  useEffect(() => {
    if (isAnnouncementModalOpen && activeUnseenAnnouncements.length === 0) {
      closeAnnouncementModal();
    }
  }, [isAnnouncementModalOpen, activeUnseenAnnouncements, closeAnnouncementModal]);

  // Consume preselectedAgentId from Agents flow on first mount
  useEffect(() => {
    const { preselectedAgentId, clearPreselectedAgent, preselectedPromptText, clearPreselectedPromptText } = useAgentsStore.getState();

    // Preselect agent tag
    if (preselectedAgentId) {
      if (preselectedAgentId === 'image') {
        setSelectedAgents(conversationId, ['image']);
      } else if (selectedAgents.includes('image')) {
        setSelectedAgents(conversationId, [preselectedAgentId]);
      } else if (!selectedAgents.includes(preselectedAgentId)) {
        setSelectedAgents(conversationId, [...selectedAgents, preselectedAgentId]);
      }
      clearPreselectedAgent();
    }

    // Pre-populate chat input with example prompt text
    if (preselectedPromptText) {
      setInputValue(conversationId, preselectedPromptText);
      clearPreselectedPromptText();
    }
  }, [conversationId, selectedAgents, setInputValue, setSelectedAgents]);

  // Helper function to create optimistic user message
  const createOptimisticUserMessage = useCallback((content: string): ConversationMessage => ({
    id: crypto.randomUUID(),
    role: 'user',
    content,
    timestamp: new Date(),
    actions: {
      canEdit: true,
      canDelete: true,
      canRegenerate: false,
      canPlayAudio: false,
    },
  }), []);

  // Helper function to create optimistic assistant message
  const createOptimisticAssistantMessage = useCallback((): ConversationMessage => ({
    id: crypto.randomUUID(),
    role: 'assistant',
    content: '', // Will be filled by real response
    timestamp: new Date(),
    actions: {
      canEdit: false,
      canDelete: true,
      canRegenerate: true,
      canPlayAudio: true,
    },
    metadata: {
      // Agent info will be determined by the orchestrator
    },
  }), []);

  /**
   * Initialize the selected model based on conversation history
   * If conversation history exists, use the model from the last message
   * Otherwise, fall back to the default model
   */
  const initializeSelectedModel = useCallback(() => {
    if (query?.data && query.data.conversationHistory.length > 0) {
      setSelectedModel(conversationId, query.data.conversationHistory[query.data.conversationHistory.length - 1].gptVersion ?? DEFAULT_MODEL);
    } else {
      setSelectedModel(conversationId, DEFAULT_MODEL);
    }
  }, [query?.data, setSelectedModel, conversationId]);

  useEffect(() => {
    initializeSelectedModel();
  }, [initializeSelectedModel]);

  // Inline upload hook (for drag/paste quick flow)
  const { uploadFiles: uploadFilesQuick, isUploading: isInlineUploading } = useFileUpload(conversationId, {
    onUploadSuccess: (response) => {
      // Success notification
      showNotification('Your files have been uploaded');
    },
    onUploadError: (err) => {
      setDropErrorMessage(typeof err?.userMessage === 'string' ? err.userMessage : 'Upload failed. Please try again.');
    },
  });

  // Inline delete hook for quick delete flow
  const { deleteFiles: deleteFilesQuick, isDeleting: isInlineDeleting } = useFileDelete(conversationId);

  // Sync conversation files when server data is available (eventual consistency)
  useEffect(() => {
    if (query?.data) {
      const serverFiles = getAllConversationFiles(query.data);
      // Always sync, even when empty — otherwise stale local fileEntries
      // can survive the last deletion in the store, modal, and generated prompt.
      syncConversationFiles(conversationId, serverFiles);
    }
  }, [query?.data, conversationId, syncConversationFiles]);

  // Helpers: drag detection (file-kind only)
  const isFileDragEvent = (e: React.DragEvent) => {
    const items = e.dataTransfer?.items;
    if (!items) return false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') return true;
    }
    return false;
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    // Block overlay while any upload or delete is in progress
    if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting) return;
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  }, [isInlineUploading, isModalUploading, isInlineDeleting, isModalDeleting]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting) return;
  }, [isInlineUploading, isModalUploading, isInlineDeleting, isModalDeleting]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
      setDropErrorMessage(undefined);
    }
  }, []);

  const closeDropOverlay = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    setDropErrorMessage(undefined);
  }, []);

  const processAndUpload = useCallback(async (incomingFiles: File[], source?: 'drop' | 'paste') => {
    // Prevent concurrent uploads from inline flow
    if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting) {
      showNotification('Please wait for the current upload to finish.');
      return;
    }
    // Clipboard images are always named e.g. "image.png"; rename collisions so a
    // second paste/drop doesn't overwrite an existing file.
    const files = getFilesWithUniqueNames(incomingFiles, getConversationFiles(conversationId));
    const existingCount = getConversationFiles(conversationId).length;
    const limits = validateAgainstLimits(files, existingCount);
    if (!limits.valid) {
      showNotification(limits.message || 'File validation failed.');
      setDropErrorMessage(limits.message);
      return;
    }

    const typeRes = validateFileTypes(files, getConversationFiles(conversationId));
    if (!typeRes.valid) {
      const msg = typeRes.message || 'File type validation failed.';
      showNotification(msg);
      setDropErrorMessage(msg);
      return;
    }

    try {
      // Optimistic: immediately queue files, mark uploading, and show in indicators
      files.forEach((f) => {
        addOrQueueFile(conversationId, f.name, 'inline');
        markFileUploading(conversationId, f.name);
      });

      const resp = await uploadFilesQuick({ files, conversationId, deletedFiles: [] });
      // Mark files as uploaded on success
      files.forEach((f) => markFileUploaded(conversationId, f.name));
      setConversationUploadSession(conversationId, resp.upload_session_id);
      // Also set global uploadSessionId so chat payload includes it
      setUploadSessionId(resp.upload_session_id);
      // Analytics: track inline upload by source
      try {
        trackComponentInteraction(
          'ChatInputSimple',
          source === 'paste' ? 'Inline Paste Upload' : 'Inline Drop Upload',
          source === 'paste' ? 'paste' : 'dropzone',
          'upload',
          'file_upload',
          `/chat/${conversationId}`
        );
      } catch {
        // no-op
      }
      // Reference message handling is done in existing effect
      closeDropOverlay();
    } catch (e) {
      // Mark error and rollback optimistic additions on failure
      files.forEach((f) => {
        markFileError(conversationId, f.name);
        removeConversationFile(conversationId, f.name);
        removePendingFileForMessage(conversationId, f.name);
        removeFile(conversationId, f.name);
      });
      // Error handled via hook callback; keep overlay to show error
    }
  }, [
    conversationId,
    getConversationFiles,
    uploadFilesQuick,
    addOrQueueFile,
    markFileUploading,
    markFileUploaded,
    markFileError,
    removeConversationFile,
    removePendingFileForMessage,
    removeFile,
    setConversationUploadSession,
    setUploadSessionId,
    closeDropOverlay,
    isInlineUploading,
    isModalUploading,
    isInlineDeleting,
    isModalDeleting,
  ]);

  const handleDropzoneDrop = useCallback((files: File[]) => {
    void processAndUpload(files, 'drop');
  }, [processAndUpload]);

  const handleDropzoneReject = useCallback(() => {
    setDropErrorMessage('Unsupported file type or size.');
  }, []);

  // Sync message count when server data is available (for validation only)
  useEffect(() => {
    if (query?.data) {
      const serverMessageCount = getUserMessageCount(query.data.conversationHistory);
      const currentCount = getMessageCount(conversationId);

      // Update local count if it differs from server
      if (currentCount !== serverMessageCount) {
        setMessageCount(conversationId, serverMessageCount);
      }
    }
  }, [query?.data, conversationId, getMessageCount, setMessageCount]);


  // Helper function to create reference message when files are uploaded
  const getReferenceMessage = useCallback((files: string[]) => {
    if (files.length === 0) return "";
    const filenames = files.join(', ') || '';
    return filenames ? `Please reference the following document(s) in your response (${filenames})\n` : '';
  }, []);

  const handleSubmit = useCallback(async () => {
    // Block sending while files are uploading, deleting, or being indexed.
    if (disabled) return;
    if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting || fileProcessingStatus === 'processing') return;
    if (!inputValue.trim() || !canSend || (FEATURES.showDigitalMaintenanceCheckResult && (isMessageSending || isChatSendInFlight(conversationId)))) return;

    // Track agent chat start if agents are selected
    if (effectiveSelectedAgents.length > 0) {
      effectiveSelectedAgents.forEach(agentId => {
        const agentDisplayName = getAgentDisplayName(agentId);
        trackComponentInteraction(
          'AgentsModal',
          agentDisplayName,
          'agent',
          'chat',
          'agent_chat_started'
        );
      });

      // Track Code Interpreter execution initiated
      if (effectiveSelectedAgents.includes('codeinterpreter')) {
        trackComponentInteraction(
          'ChatInput',
          'Code Interpreter Run',
          'action',
          'submit',
          'code_interpreter_execute'
        );
      }
    }

    // Track send button click
    trackComponentInteraction(
      'ChatInputSimple',
      'Send Message',
      'button',
      'click',
      'send_message'
    );

    const messageContent = inputValue.trim();

    // Loading state is now managed by the TanStack Query mutation

    // Create optimistic messages for home screen
    const userMessage = createOptimisticUserMessage(messageContent);
    const assistantMessage = createOptimisticAssistantMessage();

    // Set up home screen conversation
    setHomeScreenConversation({
      id: conversationId,
      userMessage,
      assistantMessage,
      createdAt: new Date(),
    });

    // Clear input immediately for better UX
    clearInput(conversationId);

    try {
      const response = await sendMessage(messageContent, {
        model: selectedModel,
        agents: effectiveSelectedAgents,
      });

      // Update with real assistant response
      updateHomeScreenConversation({
        assistantMessage: {
          ...assistantMessage,
          content: response.answer,
          toolCalls: response.toolCalls || [],
          metadata: {
            ...assistantMessage.metadata,
            tokens: response.token_count,
            dataPoints: response.data_points || [],
            citations: response.data_points?.map((point, index) => ({
              id: crypto.randomUUID(),
              index: index + 1,
              title: point.title || 'Source',
              text: point.title || 'Source', // Display text for the citation
              source: point.source || 'Unknown',
              url: point.url || point.source,
              snippet: point.content?.substring(0, 200),
            })) || [],
          },
        },
      });

      // Reset input to show reference message if files are still present
      const currentConversationFiles = getConversationFiles(conversationId);
      if (currentConversationFiles.length > 0) {
        const referenceMessage = getReferenceMessage(currentConversationFiles);
        setInputValue(conversationId, referenceMessage);
      }

      // Don't call onMessageSent immediately - let user see the conversation first
      // onMessageSent?.(conversationId);

     } catch (error) {
      // check if its cancellation error
      const isCancellationError =
        error instanceof Error && (
          error.name === 'AbortError' ||
          error.message.toLowerCase().includes('cancelled') ||
          error.message.toLowerCase().includes('aborted') ||
          error.message.toLowerCase().includes('cancel')
        );

      // if its first time then just clear it and display the welcome msg
      if (isCancellationError) {
        console.info('Connection cancelled');
        updateHomeScreenConversation({
          error: 'Connection cancelled',
        });
        setInputValue(conversationId, messageContent);
      } else {
        console.error('Failed to send message:', error);
        // otherwise display error msg
        updateHomeScreenConversation({
          error: error instanceof Error ? error.message : 'Failed to send message',
        });
        setInputValue(conversationId, messageContent);
      }
    } finally {
      // Loading state is now automatically managed by the TanStack Query mutation
    }
  }, [
    disabled,
    inputValue,
    canSend,
    isMessageSending,
    isInlineUploading,
    isModalUploading,
    isInlineDeleting,
    isModalDeleting,
    fileProcessingStatus,
    sendMessage,
    selectedModel,
    effectiveSelectedAgents,
    clearInput,
    conversationId,
    createOptimisticUserMessage,
    createOptimisticAssistantMessage,
    setHomeScreenConversation,
    updateHomeScreenConversation,
    setInputValue,
    getConversationFiles,
    getReferenceMessage,
  ]);

  // Stop thinking handler that reuses existing infrastructure
  const handleStopThinking = useCallback(() => {
    // Check if we're currently editing a message
    if (isEditingMessage) {
      // Use edit message abort method
      abortEditRequest();
    } else {
      // Use regular message sending abort method
      abortRequest();
    }
    // Stop streaming in both cases
    stopThinking();
  }, [isEditingMessage, abortEditRequest, abortRequest, stopThinking]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.shiftKey) {
      // Allow new line with Shift+Enter, including while the agent selector is open.
      return;
    }

    const isAgentSelectorKey = event.key === 'ArrowUp'
      || event.key === 'ArrowDown'
      || event.key === 'Enter';
    if (isAtAgentsModalOpen && isAgentSelectorKey) {
      const wasHandled = atAgentsModalRef.current?.handleKeyDown(event) ?? false;
      if (wasHandled) {
        return;
      }
    }

    if (event.key === 'Enter') {
      // Send when Enter was not handled by the agent selector.
      event.preventDefault();
      if (!(isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting)) {
        handleSubmit();
      }
    }
  }, [handleSubmit, isAtAgentsModalOpen, atAgentsModalRef, isInlineUploading, isModalUploading, isInlineDeleting, isModalDeleting]);

  const handleAgentSelection = useCallback(() => {
    trackComponentInteraction(
      'Navigation',
      'Agents',
      'menu',
      'click',
      'agents_open'
    );
    toggleAgentsModal();
  }, [toggleAgentsModal]);

  const handleAgentSelect = useCallback((agent: Agent) => {
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name,
      'agent',
      'select',
      'agent_selected'
    );

    // Enforce single-agent routing when image is selected (direct router only)
    if (agent.id === 'image') {
      setSelectedAgents(conversationId, ['image']);
    } else if (selectedAgents.includes('image')) {
      setSelectedAgents(conversationId, [agent.id]);
    } else {
      const nextAgents = selectedAgents.includes(agent.id)
        ? selectedAgents
        : [...selectedAgents, agent.id];
      setSelectedAgents(conversationId, nextAgents);
    }

    closeAgentsModal();
    closeAtAgentsModal();
  }, [setSelectedAgents, closeAgentsModal, conversationId, closeAtAgentsModal, selectedAgents]);

  const handleAgentRemove = useCallback((agentId: string) => {
    removeSelectedAgent(conversationId, agentId);
  }, [removeSelectedAgent, conversationId]);




  const handleModelSelection = useCallback(() => {
    trackComponentInteraction(
      'ChatInput',
      'Models',
      'button',
      'click',
      'model_selection_open'
    );
    toggleModelsModal();
  }, [toggleModelsModal]);

  const handleModelSelect = useCallback((model: Model) => {
    trackComponentInteraction(
      'ModelsModal',
      model.name,
      'model',
      'select',
      'model_selected'
    );
    setSelectedModel(conversationId, model.id);
    closeModelsModal();
  }, [setSelectedModel, closeModelsModal, conversationId]);

  // Helper function to get model display name
  const getModelDisplayName = useCallback((modelId: string) => {
    return getSelectableModelDisplayName(modelId);
  }, []);

  const handlePromptLibraryOpen = useCallback(() => {
    trackComponentInteraction(
      'ChatInput',
      'Prompt Library',
      'button',
      'click',
      'prompt_library_open'
    );
    openPromptLibraryModal();
  }, [openPromptLibraryModal]);

  const handlePromptSelect = useCallback((prompt: PromptTemplate) => {
    setInputValue(conversationId, prompt.text);
    closePromptLibraryModal();
  }, [setInputValue, closePromptLibraryModal, conversationId]);

  const handleAttachOpen = useCallback(() => {
    trackComponentInteraction(
      'ChatInput',
      'Attachment',
      'button',
      'click',
      'attachment_open'
    );
    openAttachModal();
  }, [openAttachModal]);

  const handleFilesSelected = useCallback((files: File[]) => {
    files.forEach(file => addAttachment(file, conversationId));
  }, [addAttachment, conversationId]);

  const handleUpload = useCallback((uploadSessionId: string) => {
      trackComponentInteraction(
      'File Upload',
      'Upload files',
      'button',
      'click',
      'file_upload'
    );

    closeAttachModal();
    // Upload session ID will be used during message sending
  }, [closeAttachModal]);

  const handleAttachClose = useCallback(() => {
    clearAttachments();
    closeAttachModal();
  }, [clearAttachments, closeAttachModal]);

  const handleRemoveUploadedFile = useCallback(async (filename: string) => {
    // Prevent concurrent delete while any upload/delete or server-side processing
    // is in progress. isFileProcessing is read directly from the store (not from
    // the React closure) because setFileProcessingStatus is called synchronously
    // in this handler — a second rapid click would see the stale closure value
    // of false and bypass the guard before React has re-rendered.
    const isCurrentlyProcessing =
      useChatStore.getState().fileProcessingStatus[conversationId]?.status === 'processing';
    const hasDeletingEntry = useChatStore.getState()
      .getConversationFileEntries(conversationId)
      .some((file) => file.status === 'deleting');
    if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting || isCurrentlyProcessing || hasDeletingEntry) {
      showNotification('Please wait for the current operation to finish.');
      return;
    }

    try {
      // Mark deleting synchronously. The mutation's pending state locks the
      // input until the backend acknowledges the operation; only then does
      // processing polling start.
      markFileDeleting(conversationId, filename);

      await deleteFilesQuick({ conversationId, filenames: [filename] });

      // Do NOT eagerly remove the file here — the completion effect
      // (fileProcessingStatus === 'complete') handles cleanup once the
      // server confirms the deletion is finished.  This keeps the
      // processing banner visible and the textarea disabled until then,
      // matching the modal-delete behaviour.
    } catch (e) {
      // Revert to uploaded state on failure
      markFileUploaded(conversationId, filename);
    }
  }, [
    conversationId,
    isInlineUploading,
    isModalUploading,
    isInlineDeleting,
    isModalDeleting,
    markFileDeleting,
    deleteFilesQuick,
    markFileUploaded,
  ]);

  // Get pending files for next message (input indicators)
  const pendingFiles = getPendingFilesForMessage(conversationId);

  // Subscribe to the derived list itself. Calling the getter imperatively does
  // not subscribe this component to conversationFiles updates, which leaves the
  // indicators and generated prompt stale until an unrelated re-render.
  const conversationFiles = useChatStore(
    useShallow((state) => state.getConversationFiles(conversationId)),
  );

  // Ref to track current input value for comparison
  const currentInputRef = useRef(inputValue);
  const previousConversationFilesRef = useRef<string[]>([...conversationFiles]);

  const referenceMessagePrefix = 'Please reference the following document(s) in your response';

  // Set reference message when files are uploaded or when input is empty and files are present
  useEffect(() => {
    const previousFiles = previousConversationFilesRef.current;
    const filesChanged =
      previousFiles.length !== conversationFiles.length ||
      previousFiles.some((file, index) => conversationFiles[index] !== file);
    const nextFilesSnapshot = [...conversationFiles];

    // Bail out if nothing about the file list changed so manual edits stay intact
    if (!filesChanged) {
      previousConversationFilesRef.current = nextFilesSnapshot;
      return;
    }

    const currentValue = currentInputRef.current;
    const hasReferenceMessage = currentValue.includes(referenceMessagePrefix);

    const referenceMessagePattern = /Please reference the following document\(s\) in your response \((?:[^()]|\([^()]*\))*\)(?:\n)?/;

    if (conversationFiles.length > 0) {
      const referenceMessage = getReferenceMessage(conversationFiles);

      if (hasReferenceMessage) {
        // Update existing reference message to reflect current files
        const updatedValue = currentValue.replace(referenceMessagePattern, referenceMessage);
        if (updatedValue !== currentValue) {
          setInputValue(conversationId, updatedValue);
        }
      } else if (currentValue.trim() === '' && previousFiles.length === 0) {
        // Only add reference message if input is completely empty (user just started typing)
        // This prevents adding it back while user is actively editing or after manual removal
        setInputValue(conversationId, referenceMessage);
      }
    } else if (hasReferenceMessage) {
      // Remove reference message if no files are present
      const updatedValue = currentValue.replace(referenceMessagePattern, '');
      if (updatedValue !== currentValue) {
        setInputValue(conversationId, updatedValue);
      }
    }

    previousConversationFilesRef.current = nextFilesSnapshot;
  }, [conversationFiles, getReferenceMessage, setInputValue, conversationId]);

  // Update ref when input value changes
  useEffect(() => {
    currentInputRef.current = inputValue;
  }, [inputValue]);

  // When text is injected programmatically (reference prompt, follow-up question, etc.),
  // move the caret to the end so the user can type immediately after the preset text.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (!inputValue) return;
    if (document.activeElement !== textarea) return;

    const caretAtStart = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
    const entireValueSelected = textarea.selectionStart === 0 && textarea.selectionEnd === textarea.value.length;
    if (!caretAtStart && !entireValueSelected) return;

    requestAnimationFrame(() => {
      const activeTextarea = textareaRef.current;
      if (!activeTextarea) return;
      if (document.activeElement !== activeTextarea) return;
      if (activeTextarea.value !== inputValue) return;
      const nextPosition = Math.min(activeTextarea.value.length, inputValue.length);
      activeTextarea.setSelectionRange(nextPosition, nextPosition);
      activeTextarea.scrollTop = activeTextarea.scrollHeight;
    });
  }, [inputValue]);

  // Helper function to get agent display name
  const getAgentDisplayName = useCallback((agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : agentId;
  }, [agents]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        reset();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error, reset]);

  // Poll upload status — must run here because the index page does not mount useConversationDetailWithStore
  useUploadStatusPoll(conversationId);

  const isFileProcessing = fileProcessingStatus === 'processing';
  // Any active file operation (upload/delete, inline or modal, or server-side
  // indexing) should lock the input field and show the processing banner.
  const isAnyFileOperationInProgress = isInlineUploading || isInlineDeleting || isModalUploading || isModalDeleting || isFileProcessing;
  const fileOperationBannerTitle = (isInlineUploading || isModalUploading)
    ? 'Your file is being uploaded'
    : (isInlineDeleting || isModalDeleting)
      ? 'Your file is being deleted'
      : 'Your file is being processed';
  const canSendMessage = !disabled && canSend && inputValue.trim().length > 0 && !isMessageSending && !isOverLimit && !isInlineUploading && !isModalUploading && !isInlineDeleting && !isModalDeleting && !isFileProcessing;

  // 5-minute fallback: if file processing status hasn't resolved via WS,
  // refetch conversation detail to check server-side status
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isFileProcessing) return;
    const FALLBACK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.detail(conversationId),
      });
    }, FALLBACK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isFileProcessing, conversationId, queryClient]);

  // Show success notification and clear status when processing completes
  useEffect(() => {
    if (fileProcessingStatus === 'complete') {
      // A deleting entry is retained while the backend removes index and
      // metadata records. Now that completion has been confirmed, remove it
      // from local file state and message indicators.
      getConversationFileEntries(conversationId)
        .filter((file) => file.status === 'deleting')
        .forEach((file) => {
          removeConversationFile(conversationId, file.name);
          removePendingFileForMessage(conversationId, file.name);
          removeFile(conversationId, file.name);
        });

      showNotification('Your files are ready');
      // Completion is emitted only after the server has persisted the finished
      // file operation, so active views can safely reload the authoritative list.
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.detail(conversationId),
        refetchType: 'active',
      });
      // Clear the status after a short delay so the notification is visible
      const timer = setTimeout(() => {
        useChatStore.getState().clearFileProcessingStatus(conversationId);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    fileProcessingStatus,
    conversationId,
    getConversationFileEntries,
    removeConversationFile,
    removePendingFileForMessage,
    removeFile,
    queryClient,
  ]);

  return (
    <Stack gap={0} style={{ width: '100%', maxWidth: 918 }}>
      {/* Error Alert */}
      {error && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title={(error instanceof Error ? error.message : String(error)).toLowerCase().includes('cancel') ? 'Connection cancelled' : 'Error'}
          color={(error instanceof Error ? error.message : String(error)).toLowerCase().includes('cancel') ? 'yellow' : 'red'}
          variant="light"
          className={classes.errorAlert}
        >
          {error instanceof Error ? error.message : String(error)}
        </Alert>
      )}

      {/* File Processing Banner — shown for any active file operation */}
      {isAnyFileOperationInProgress && (
        <Alert
          icon={<Loader size={16} color="white" />}
          title={fileOperationBannerTitle}
          color="blue"
          variant="filled"
          className={classes.errorAlert}
        >
          Please wait — the input will be unlocked once the operation is complete.
        </Alert>
      )}

      {/* File Processing Failure Banner */}
      {(fileProcessingStatus === 'failed' || fileProcessingStatus === 'partial') && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title={fileProcessingStatus === 'partial' ? 'Some files could not be processed' : 'File processing failed'}
          color={fileProcessingStatus === 'partial' ? 'yellow' : 'red'}
          variant="light"
          className={classes.errorAlert}
          withCloseButton
          onClose={() => useChatStore.getState().clearFileProcessingStatus(conversationId)}
        >
          {fileProcessingStatus === 'partial'
            ? 'Successfully processed files are available. Try uploading the failed files again.'
            : 'Processing failed. Please try uploading your files again.'}
        </Alert>
      )}


      {isAtAgentsModalOpen && (
        <Popover
          opened={isAtAgentsModalOpen}
          onClose={closeAtAgentsModal}
          position="top-start"
          width={240}
          withinPortal
          shadow="md"
          styles={{
            dropdown: {
              border: '1px solid var(--fonterra-border-accent)',
              borderRadius: '6px',
              backgroundColor: 'var(--fonterra-surface-popover)',
              padding: 0,
              boxShadow: 'var(--fonterra-shadow-popover)',
            }
          }}
        >
          <Popover.Target>
            <div style={{ position: 'relative', bottom: 0, left: 0, height: 0 }} />
          </Popover.Target>
          <Popover.Dropdown>
            <AtAgentsModal
              ref={atAgentsModalRef}
              onAgentSelect={handleAgentSelect}
              onAgentRemove={handleAgentRemove}
              selectedAgents={selectedAgents}
              mentionFilter={mentionFilter}
              removeAtInput={removeAtInput}
            />
          </Popover.Dropdown>
        </Popover>
      )}


      {/* Input Field */}
      <Text className={classes.globalDisclaimer}>
        Co-op GPT is AI and can make mistakes.
      </Text>
      <Box
        className={classes.inputContainer}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => {
          // Allow dropping directly onto the input area even if overlay hasn't rendered yet
          if (isFileDragEvent(e as unknown as React.DragEvent)) {
            e.preventDefault();
            e.stopPropagation();
            if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting) {
              showNotification('Please wait for the current upload to finish.');
              return;
            }
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length > 0) {
              void processAndUpload(files);
            }
            closeDropOverlay();
          }
        }}
        style={{ position: 'relative' }}
      >
        {/* Input Section: tags row above textarea */}
        <Box className={classes.inputSection}>
          {/* Tags row - agent tag + uploaded file indicators */}
          {(selectedAgents.length > 0 || conversationFiles.length > 0) && (
            <Box className={classes.tagsRow}>
              {selectedAgents.length > 0 && (
                <Group gap={6}>
                  {selectedAgents.map((agentId) => (
                    <Box
                      key={agentId}
                      className={classes.inlineAgentTag}
                      onMouseEnter={() => setIsAgentTagHovered(true)}
                      onMouseLeave={() => setIsAgentTagHovered(false)}
                      onClick={() => handleAgentRemove(agentId)}
                    >
                      {isAgentTagHovered ? (
                        <Icon name="minus" size={18} />
                      ) : (
                        <Icon name="at" size={18} />
                      )}
                      <Text size="15px" fw={400} style={{ lineHeight: '1.6em' }}>
                        {getAgentDisplayName(agentId)}
                      </Text>
                    </Box>
                  ))}

                </Group>
              )}
              {conversationFiles.length > 0 && (
                <UploadedFileIndicators
                  conversationId={conversationId}
                  onRemove={handleRemoveUploadedFile}
                />
              )}
            </Box>
          )}
          <Box className={classes.textareaWrapper} data-dragging={isDraggingFiles ? 'true' : undefined}>
            <Textarea
              ref={setRef}
              value={inputValue}
              onChange={(event) => {
                const newValue = event.target.value;
                const newCount = newValue.length;
                const nextCaretPosition = event.target.selectionStart ?? newCount;

                // Truncate if over limit (paste handling)
                if (newCount > 400000) {
                  const truncatedValue = newValue.substring(0, 400000);
                  setInputValue(conversationId, truncatedValue);
                  setMentionCaretPosition(Math.min(nextCaretPosition, truncatedValue.length));
                } else {
                  setInputValue(conversationId, newValue);
                  setMentionCaretPosition(nextCaretPosition);
                }
              }}
              placeholder={isDraggingFiles ? '' : isAnyFileOperationInProgress ? 'Waiting for file operation to complete…' : "Hi there! How can I help you today?"}
              disabled={disabled || isMessageSending || isAnyFileOperationInProgress}
              onKeyDown={handleKeyDown}
              onSelect={(event) => {
                setMentionCaretPosition(event.currentTarget.selectionStart);
              }}
              onFocus={(event) => {
                const element = event.currentTarget;
                const { value } = element;
                if (!value) return;
                const caretAtStart = element.selectionStart === 0 && element.selectionEnd === 0;
                if (!caretAtStart) return;
                requestAnimationFrame(() => {
                  const activeTextarea = textareaRef.current;
                  if (!activeTextarea) return;
                  if (document.activeElement !== activeTextarea) return;
                  if (activeTextarea !== element) return;
                  const nextPosition = activeTextarea.value.length;
                  activeTextarea.setSelectionRange(nextPosition, nextPosition);
                  setMentionCaretPosition(nextPosition);
                  activeTextarea.scrollTop = activeTextarea.scrollHeight;
                });
              }}
              onPaste={(event) => {
                if (isInlineUploading || isModalUploading || isInlineDeleting || isModalDeleting) {
                  event.preventDefault();
                  showNotification('Please wait for the current upload to finish.');
                  return;
                }
                const clipboard = event.clipboardData;
                const files = clipboard?.files;
                if (!files || files.length === 0) return;
                const realFiles = Array.from(files).filter((f) => f.size > 0);
                if (realFiles.length === 0) return;
                const types = clipboard.types ? Array.from(clipboard.types) : [];
                const hasPlainText = types.includes('text/plain');
                if (!hasPlainText) {
                  event.preventDefault();
                }
                void processAndUpload(realFiles, 'paste');
              }}
              autosize
              minRows={1}
              maxRows={6}
              className={classes.textInputField}
              styles={{
                input: {
                  background: 'transparent',
                  border: 'none',
                  padding: '6px',
                  resize: 'none',
                  outline: 'none',
                  lineHeight: 1.6,
                  fontSize: '15px',
                  fontFamily: 'Inter, sans-serif',
                  color: isDraggingFiles ? 'transparent' : 'var(--fonterra-input-text)',
                  WebkitTextFillColor: isDraggingFiles ? 'transparent' as any : undefined,
                  caretColor: isDraggingFiles ? 'transparent' : undefined,
                  pointerEvents: isDraggingFiles ? 'none' : undefined,
                },
              }}
              spellCheck={isDraggingFiles ? false : undefined}
            />
            {isDraggingFiles && (
              <FileDropzone
                onDropValid={handleDropzoneDrop}
                onDropRejected={handleDropzoneReject}
                disabled={disabled || isMessageSending || isInlineUploading || isInlineDeleting}
                isUploading={isInlineUploading}
                maxFilesLeft={Math.max(0, MAX_FILES - getConversationFiles(conversationId).length)}
                errorMessage={dropErrorMessage}
                onCloseOverlay={closeDropOverlay}
                accept={getDropzoneAccept()}
                maxSize={MAX_FILE_SIZE_BYTES}
              />
            )}
            {/* Counter Row */}
            {showCharacterCounter && (
              <Group justify="space-between" w="100%" className={classes.counterRow}>
                {/* Character Counter */}
                {showCharacterCounter && (
                  <Text
                    size="15px"
                    fw={400}
                    c={characterCount >= 400000 ? "red" : undefined}
                    className={classes.characterCounter}
                  >
                    {characterCount} / 400000 characters
                  </Text>
                )}

              </Group>
            )}
          </Box>
        </Box>
      </Box>

      {/* Controls */}
      {showControls && (
        <Group justify="space-between" w="100%" style={{ marginTop: 'var(--input-spacing)' }}>
          <Group gap={0}>
            <Button
              className={classes.controlButton}
              leftSection={<Icon name='attach' size={24} />}
              styles={{ section: { marginRight: '9px' } }}
              disabled={disabled || isMessageSending || isAnyFileOperationInProgress}
              onClick={handleAttachOpen}
              aria-label="Attach"
            >
              Attach
            </Button>
            <Popover
              width={240}
              position="top-start"
              offset={12}
              withinPortal
              middlewares={{
                flip: true,
                shift: { padding: CONTROL_POPOVER_VIEWPORT_PADDING },
                size: { padding: CONTROL_POPOVER_VIEWPORT_PADDING },
              }}
              shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
              opened={isAgentsModalOpen}
              onChange={closeAgentsModal}
              styles={{
                dropdown: {
                  border: '1px solid var(--fonterra-border-accent)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--fonterra-surface-popover)',
                  padding: 0,
                  boxShadow: 'var(--fonterra-shadow-popover)',
                  overflow: 'hidden',
                }
              }}
            >
              <Popover.Target>
                <Button
                  className={classes.controlButton}
                  leftSection={<Icon name="at" size={24} />}
                  styles={{ section: { marginRight: '9px' } }}
                  disabled={disabled || isMessageSending}
                  onClick={handleAgentSelection}
                  aria-label="Agents"
                >
                  Agents
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <AgentsModal
                  onAgentSelect={handleAgentSelect}
                  onAgentRemove={handleAgentRemove}
                  selectedAgents={selectedAgents}
                />
              </Popover.Dropdown>
            </Popover>

            <Popover
              width={240}
              position="top-start"
              offset={12}
              withinPortal
              middlewares={{
                flip: true,
                shift: { padding: CONTROL_POPOVER_VIEWPORT_PADDING },
                size: { padding: CONTROL_POPOVER_VIEWPORT_PADDING },
              }}
              shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
              opened={isModelsModalOpen}
              onChange={closeModelsModal}
              styles={{
                dropdown: {
                  border: '1px solid var(--fonterra-border-accent)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--fonterra-surface-popover)',
                  padding: 0,
                  boxShadow: 'var(--fonterra-shadow-popover)',
                  overflow: 'hidden',
                }
              }}
            >
              <Popover.Target>
                <Button
                  className={classes.modelButton}
                  leftSection={<Icon name="head" size={24} />}
                  styles={{ section: { marginRight: '9px' } }}
                  disabled={disabled || isMessageSending}
                  onClick={handleModelSelection}
                  aria-label="Model selection"
                >
                  {getModelDisplayName(selectedModel)}
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <ModelsModal
                  selectedModel={selectedModel}
                  onModelSelect={handleModelSelect}
                />
              </Popover.Dropdown>
            </Popover>
            <Button
              className={classes.controlButton}
              leftSection={<Icon name="bulb" size={24} />}
              styles={{ section: { marginRight: '9px' } }}
              disabled={disabled || isMessageSending}
              onClick={handlePromptLibraryOpen}
              aria-label="Prompt Library"
            >
              Prompt Library
            </Button>
          </Group>

          {/* Send/Stop button */}
          <ActionIcon
            className={classes.controlSendButton}
            aria-label={isStreaming ? 'Stop' : 'Send'}
            onClick={isStreaming ? handleStopThinking : handleSubmit}
            disabled={!isStreaming && !canSendMessage}
            variant="filled"
            radius="md"
            styles={{
              root: {
                backgroundColor: 'var(--fonterra-border-accent)',
                color: 'var(--fonterra-text-on-accent)',
                border: '1px solid transparent',
                borderRadius: '6px',
                padding: '6px',
                width: '42px',
                height: '42px',
                minWidth: '42px',
                transition: 'all 150ms ease',
                '&:hover': {
                  backgroundColor: 'var(--fonterra-accent-positive-hover)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--fonterra-accent-positive-disabled)',
                  color: 'var(--fonterra-text-on-accent)',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                },
              },
            }}
          >
            {isStreaming ? (
              <IconX size={24} color="#FFFFFF" />
            ) : isMessageSending ? (
              <Loader size={24} color="white" />
            ) : (
              <Icon name="send" size={24} color="#FFFFFF" />
            )}
          </ActionIcon>
        </Group>
      )}

      {/* Announcements Modal */}
      <Modal
        opened={isAnnouncementModalOpen}
        onClose={handleAnnouncementClose}
        size="auto"
        centered
        withCloseButton={false}
        padding="sm"
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        {firstUnseenAnnouncement && (
          <AnnouncementModal
            announcement={firstUnseenAnnouncement}
            onClose={handleAnnouncementClose}
          />
        )}
      </Modal>

      {/* Prompt Library Modal */}
      <Modal
        opened={isPromptLibraryModalOpen}
        onClose={closePromptLibraryModal}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <PromptLibraryModal
          onPromptSelect={handlePromptSelect}
          onClose={closePromptLibraryModal}
        />
      </Modal>

      {/* Attach Modal */}
      <Modal
        opened={isAttachModalOpen}
        onClose={() => {
          // Only close if not uploading, deleting, or processing (legacy)
          if (!isModalUploading && !isModalDeleting) {
            handleAttachClose();
          }
        }}
        closeOnClickOutside={!(isModalUploading || isModalDeleting)}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <AttachModal
          conversationId={conversationId}
          onFilesSelected={handleFilesSelected}
          onUpload={handleUpload}
          onClose={handleAttachClose}
          onUploadStateChange={(isUploading: boolean, isDeleting: boolean) => {
            setIsModalUploading(isUploading);
            setIsModalDeleting(isDeleting);
          }}
        />
      </Modal>

    </Stack>
  );
});

ChatInputSimple.displayName = 'ChatInputSimple';

export type { ChatInputSimpleProps };
